import React, { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage.jsx';
import ThemeToggle from '../components/ThemeToggle.jsx';
import LanguageSelector from '../components/LanguageSelector.jsx';
import './FxLabHome.css';

const API = import.meta.env.VITE_API_URL || '';

/* Product picker → workspace panels (weekly is its own route). */
const PICKER = [
  { key: 'news', cn: '实时新闻', en: 'Real-time news', color: 'var(--a-news)' },
  { key: 'analytics', cn: '数据分析', en: 'Analytics', color: 'var(--a-data)' },
  { key: 'option-lens', cn: '期权透镜', en: 'Options Lens', color: 'var(--a-opt)' },
  { key: 'macro-pulse', cn: 'MacroPulse', en: 'MacroPulse', color: 'var(--a-macro)' },
  { key: 'fortune', cn: '每日一卦', en: 'Daily fortune', color: 'var(--a-gua)' },
  { key: 'weekly-mindmap', cn: '每周周报', en: 'Weekly report', color: 'var(--a-week)' },
];

/* 顶栏「产品」下拉 —— 四个数据面板 */
const PRODUCTS = [
  { key: 'news', cn: '实时新闻', en: 'Real-time news', color: 'var(--a-news)', dcn: 'AI 摘要，一句话读懂要闻', den: 'AI-summarized headlines' },
  { key: 'analytics', cn: '数据分析', en: 'Analytics', color: 'var(--a-data)', dcn: '价格·波动·板块热图', den: 'Price · vol · sector heat' },
  { key: 'option-lens', cn: '期权透镜', en: 'Options Lens', color: 'var(--a-opt)', dcn: '零术语看懂市场押注', den: 'Where the money is, no jargon' },
  { key: 'liquidity', cn: '流动性浮标', en: 'Liquidity', color: 'var(--a-macro)', dcn: '美联储在放水还是抽水', den: 'Fed adding or draining' },
];

/* 板块涨跌 → 热图色阶(与 fx-heat 的 hg4…hr3 一致) */
const heatCls = pct => {
  if (pct == null) return 'hn0';
  if (pct >= 1.5) return 'hg4'; if (pct >= 1.0) return 'hg3'; if (pct >= 0.5) return 'hg2'; if (pct >= 0.05) return 'hg1';
  if (pct > -0.05) return 'hn0'; if (pct >= -0.5) return 'hr1'; if (pct >= -1.0) return 'hr2'; return 'hr3';
};
const fmtPct = p => (p == null ? '—' : `${p > 0 ? '+' : p < 0 ? '−' : ''}${Math.abs(p).toFixed(2)}%`);

const CHECK = (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6"><path d="M5 12l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const FxLabHome = ({ onNavigate }) => {
  const { currentLanguage } = useLanguage();
  const cn = currentLanguage === 'zh-CN';
  const [ucTab, setUcTab] = useState(0);
  const [news, setNews] = useState(null);
  const [sectors, setSectors] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/news/latest?limit=3`);
        if (res.ok) { const data = await res.json(); if (!cancelled && data?.news) setNews(data.news.slice(0, 3)); }
      } catch { /* mini-list falls back to sample copy */ }
      try {
        const res = await fetch(`${API}/api/market/sectors`);
        if (res.ok) { const data = await res.json(); if (!cancelled && data?.sectors) setSectors(data.sectors); }
      } catch { /* heat grid falls back to sample */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const go = (key) => {
    if (key === 'weekly-mindmap') { window.location.href = '/weekly-mindmap'; return; }
    onNavigate && onNavigate(key);
  };

  const clock = ts => {
    if (!ts) return '';
    const d = new Date(ts * 1000);
    const p = n => String(n).padStart(2, '0');
    return `${p(d.getHours())}:${p(d.getMinutes())}`;
  };
  const sentCls = s => { s = (s || '').toLowerCase(); return s === 'positive' ? 'up' : s === 'negative' ? 'dn' : 'neu'; };
  const sentLbl = s => { s = (s || '').toLowerCase(); if (cn) return s === 'positive' ? '利好' : s === 'negative' ? '利空' : '中性'; return s === 'positive' ? 'Bull' : s === 'negative' ? 'Bear' : 'Neutral'; };

  const T = (zh, en) => (cn ? zh : en);

  const newsRows = news && news.length
    ? news.map(n => ({ tm: clock(n.published_at), src: n.source || 'Newswire', ti: n.title, badge: sentLbl(n.sentiment), cls: `badge--${sentCls(n.sentiment) === 'up' ? 'up' : 'neu'}` }))
    : [
      { tm: '14:32', src: 'Bloomberg', ti: T('美联储纪要：6 月降息分歧加剧', 'FOMC minutes: June-cut split widens'), badge: T('中性', 'Neutral'), cls: 'badge--neu' },
      { tm: '13:08', src: 'Reuters', ti: T('比特币再破 11 万，ETF 净流入 8.2 亿', 'Bitcoin tops $110k, ETF inflow $820M'), badge: T('利好', 'Bull'), cls: 'badge--up' },
      { tm: '09:14', src: 'CNBC', ti: T('黄金突破 2,480 美元，创历史新高', 'Gold breaks $2,480, record high'), badge: T('利好', 'Bull'), cls: 'badge--up' },
    ];

  const sampleSectors = [
    { short: 'XLK', change_pct: 1.82 }, { short: 'XLC', change_pct: 1.14 }, { short: 'XLY', change_pct: 0.58 },
    { short: 'XLF', change_pct: 0.22 }, { short: 'XLI', change_pct: 0.04 }, { short: 'XLV', change_pct: -0.31 },
    { short: 'XLP', change_pct: -0.64 }, { short: 'XLU', change_pct: -1.40 }, { short: 'XLRE', change_pct: -2.08 },
  ];
  const heatData = (sectors && sectors.length ? sectors : sampleSectors).slice(0, 9);

  const features = [
    {
      key: 'news', color: 'var(--a-news)', rev: false,
      kick: T('实时新闻', 'Real-time news'), h: T('噪音里，只留信号', 'In the noise, only the signal'),
      d: T('聚合全球主流财经媒体，AI 自动摘要成一句话，配情绪与影响标签。中英双语，每日更新。', 'Aggregates global media, AI-summarized to one line, tagged by sentiment and impact. Bilingual, daily.'),
      li: [T('一句话读懂每条要闻', 'One line per headline'), T('情绪 / 影响 / 板块标签', 'Sentiment / impact / sector tags'), T('WebSocket 实时推送', 'Real-time WebSocket push')],
      link: T('了解实时新闻 →', 'Explore news →'), tab: T('实时新闻', 'Newsfeed'),
      vis: (
        <div className="fx-fvis__body">
          {newsRows.map((r, i) => (
            <div className="fx-nrow" key={i}>
              <span className="tm">{r.tm}</span>
              <div><span className="sr">{r.src}</span><p className="ti">{r.ti}</p></div>
              <span className={`fx-badge ${r.cls}`}>{r.badge}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'analytics', color: 'var(--a-data)', rev: true,
      kick: T('数据分析', 'Analytics'), h: T('把数据说成人话', 'Data, in plain words'),
      d: T('价格、成交量、波动率、板块轮动 —— 复杂指标全部翻成结论，每个数字旁边都有一句解释。', 'Price, volume, volatility, rotation — every metric turned into a conclusion, each number explained.'),
      li: [T('价格 / K线 / 波动率', 'Price / candles / volatility'), T('板块热图一眼看轮动', 'Sector heatmap at a glance'), T('全市场速览表', 'Whole-market snapshot')],
      link: T('了解数据分析 →', 'Explore analytics →'), tab: T('板块热图', 'Sector heatmap'),
      vis: (
        <div className="fx-fvis__body">
          <div className="fx-heat">
            {heatData.map((s, i) => (
              <div className={`hc${i === 0 ? ' hc--xl' : ''} ${heatCls(s.change_pct)}`} key={s.short}>
                <s>{s.short}</s><d>{fmtPct(s.change_pct)}</d>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      key: 'option-lens', color: 'var(--a-opt)', rev: false,
      kick: T('期权透镜', 'Options Lens'), h: T('不懂期权，也看得懂', 'No options jargon required'),
      d: T('零术语。告诉你股票大概会落在哪个区间、你的目标价有多大可能、大家把钱押在哪些价位。', 'Zero jargon. The likely range, your target’s odds, and where the money is parked.'),
      li: [T('预期价格区间', 'Expected price range'), T('问问市场：目标价概率', 'Ask the market: target odds'), T('押注分布价格梯子', 'Bet-distribution ladder')],
      link: T('了解期权透镜 →', 'Explore Options Lens →'), tab: T('押注分布', 'Bet distribution'),
      vis: (
        <div className="fx-fvis__body">
          <div className="fx-lad">
            <div className="fx-lad__ax" />
            {[
              { px: '$140', l: 8, r: 24, w: '' },
              { px: '$135', l: 16, r: 92, w: 'wu' },
              { px: '$130', l: 28, r: 36, w: '' },
              { px: '$125', l: 88, r: 18, w: 'wp' },
              { px: '$120', l: 44, r: 10, w: '' },
            ].map((row, i) => (
              <div className={`fx-lrow ${row.w}`} key={i}>
                <div className="fx-ltrk l"><div className="fx-lbar" style={{ width: `${row.l}%` }} /></div>
                <div className="fx-lpx">{row.px}</div>
                <div className="fx-ltrk r"><div className="fx-lbar" style={{ width: `${row.r}%` }} /></div>
              </div>
            ))}
          </div>
          <div className="fx-lad__legend">
            <span style={{ color: 'var(--a-data)' }}>← {T('买保护', 'Protection')}</span>
            <span style={{ color: 'var(--a-opt)' }}>{T('赌涨', 'Upside')} →</span>
          </div>
        </div>
      ),
    },
  ];

  const stats = [
    { num: '12k+', lab: T('活跃散户用户', 'Active retail users') },
    { num: '6', lab: T('一体化工作台模块', 'Modules in one workspace') },
    { num: '24/7', lab: T('全球市场实时覆盖', 'Global market coverage') },
    { num: T('中·英', 'CN·EN'), lab: T('双语内容默认', 'Bilingual by default') },
  ];

  const testimonials = [
    { q: T('“以前要开五个 App 才能拼出一个判断，现在一屏就够了。新闻摘要是真的省时间。”', '“I used to juggle five apps to form a view — now one screen does it. The summaries save real time.”'), av: '陈', nm: T('陈先生', 'Mr. Chen'), rl: T('日内交易 · 3 年', 'Day trader · 3 yrs') },
    { q: T('“期权透镜第一次让我看懂了‘大家把钱押在哪’。零术语这点，做到了。”', '“Options Lens finally showed me where the money is parked. Zero jargon — they nailed it.”'), av: '李', nm: T('李女士', 'Ms. Li'), rl: T('正股持有者', 'Stock holder') },
    { q: T('“MacroPulse 把美联储那套话翻译成鹰鸽分数，还老实承认样本小 —— 这份诚实我服。”', '“MacroPulse turns Fed-speak into a hawk-dove score, and admits the sample is small. That honesty won me over.”'), av: '王', nm: T('王先生', 'Mr. Wang'), rl: T('宏观关注者', 'Macro watcher') },
  ];

  const ucTabs = [T('日内交易者', 'Day traders'), T('长线持有', 'Long-term holders'), T('期权新手', 'Options beginners'), T('宏观关注者', 'Macro watchers')];

  /* 每个使用者 → 对应板块缩略(点 tab 切换) */
  const liqMini = (
    <div className="fx-fvis__body">
      <div className="fx-liqmini">
        <div className="row">
          <span className="lab">{T('净流动性', 'Net liquidity')}</span>
          <span className="val">5.917<small>{T('万亿', 'TN')}</small></span>
          <span className="tag dn">{T('本周抽水', 'Draining')}</span>
        </div>
        <svg className="spark" viewBox="0 0 260 64" preserveAspectRatio="none">
          <polygon points="0,44 26,40 52,46 78,34 104,38 130,28 156,32 182,22 208,30 234,20 260,26 260,64 0,64" fill="var(--a-macro)" opacity="0.1" />
          <polyline points="0,44 26,40 52,46 78,34 104,38 130,28 156,32 182,22 208,30 234,20 260,26" fill="none" stroke="var(--a-macro)" strokeWidth="1.8" strokeLinejoin="round" />
          <circle cx="234" cy="20" r="3" fill="var(--a-macro)" />
        </svg>
        <div className="rowb"><span>{T('近一年 80 分位', '80th pct · 1yr')}</span><span>SOFR−IORB −1bp · {T('宽松', 'Ample')}</span></div>
      </div>
    </div>
  );
  const ucThumbs = [
    { key: 'news', name: T('实时新闻', 'News'), color: 'var(--a-news)', tab: T('实时新闻', 'Newsfeed'), cap: T('盯盘间隙扫一眼，AI 把每条要闻摘成一句话，配情绪标签。', 'A glance between trades — every headline in one AI-summarized line.'), vis: features[0].vis },
    { key: 'analytics', name: T('数据分析', 'Analytics'), color: 'var(--a-data)', tab: T('板块热图', 'Sector heatmap'), cap: T('板块热图一眼看清今天钱往哪个方向流。', 'The sector heatmap shows where money is flowing today.'), vis: features[1].vis },
    { key: 'option-lens', name: T('期权透镜', 'Options Lens'), color: 'var(--a-opt)', tab: T('押注分布', 'Bet distribution'), cap: T('不懂期权，也看得懂大家把钱押在哪些价位。', 'No options jargon — just where the money is parked.'), vis: features[2].vis },
    { key: 'liquidity', name: T('流动性浮标', 'Liquidity'), color: 'var(--a-macro)', tab: T('净流动性', 'Net liquidity'), cap: T('美联储在放水还是抽水、走廊有没有压力，一屏看清。', 'Is the Fed adding or draining, and is the corridor stressed — one screen.'), vis: liqMini },
  ];
  const uc = ucThumbs[ucTab];

  return (
    <div className="fx">
      {/* ── Nav ── */}
      <nav className="fx-nav">
        <button className="fx-brand" onClick={() => go('home')}>
          <span className="mk">临</span>{T('临象', 'LinXiang')}<span style={{ color: 'var(--t3)' }}>{T('财经', 'Finance')}</span>
        </button>
        <div className="fx-nav-links">
          <div className="fx-drop">
            <button className="fx-drop__trig">{T('产品', 'Products')} <span className="car">▾</span></button>
            <div className="fx-drop__menu">
              {PRODUCTS.map(p => (
                <button className="fx-drop__item" key={p.key} onClick={() => go(p.key)}>
                  <span className="sw" style={{ background: p.color }} />
                  <span className="tx"><span className="nm">{cn ? p.cn : p.en}</span><span className="ds">{cn ? p.dcn : p.den}</span></span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="fx-nav-right">
          <ThemeToggle />
          <LanguageSelector />
          <button className="fx-login" onClick={() => go('option-lens')}>{T('进入工作台', 'Enter app')}</button>
          <button className="fx-btn fx-btn--dark" style={{ padding: '9px 16px' }} onClick={() => go('option-lens')}>{T('免费开始', 'Get started')}</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="fx-sec">
        <div className="fx-hero">
          <div>
            <p className="fx-eyebrow">{T('面向散户的金融情报台 · RETAIL INTELLIGENCE', 'Retail intelligence desk')}</p>
            <h1>{cn ? <>看懂市场，<br /><em>只需一个工作台</em></> : <>Read the market<br /><em>from one workspace</em></>}</h1>
            <p className="fx-sub">{T('实时资讯、数据分析、期权透镜、宏观脉搏 —— 散户需要的全部信号，汇成一处，说人话。', 'News, analytics, options lens and macro pulse — every signal a retail trader needs, in one place, in plain words.')}</p>
            <div className="fx-capture">
              <button className="fx-btn fx-btn--dark" onClick={() => go('option-lens')}>{T('免费开始', 'Get started')} →</button>
              <button className="fx-btn fx-btn--ghost" onClick={() => go('news')}>{T('先逛逛 →', 'Take a look →')}</button>
            </div>
            <p className="fx-microcopy">{T('无需信用卡 · 5 分钟上手 · 已有 12,000+ 散户在用', 'No credit card · 5-min setup · 12,000+ retail users')}</p>
          </div>
          <div className="fx-hero__vis">
            <div className="fx-mock">
              <div className="fx-mock__bar"><span className="fx-dot" /><span className="fx-dot" /><span className="fx-dot" /><span className="fx-mock__tab">{T('临象财经 · 工作台', 'LinXiang · Workspace')}</span></div>
              <div className="fx-mock__body">
                <p className="fx-mock__lead">NVDA · {T('英伟达 · 预期范围', 'NVIDIA · expected range')}</p>
                <p className="fx-mock__sub">{T('到 7/18 大概率落在 $148 – $181', 'By 7/18, likely $148 – $181')}</p>
                <svg className="fx-mock__chart" viewBox="0 0 440 150" preserveAspectRatio="none">
                  <line x1="0" y1="40" x2="440" y2="40" stroke="var(--line-soft)" /><line x1="0" y1="80" x2="440" y2="80" stroke="var(--line-soft)" /><line x1="0" y1="120" x2="440" y2="120" stroke="var(--line-soft)" />
                  <polygon points="230,86 430,44 430,118" fill="currentColor" opacity="0.06" />
                  <line x1="230" y1="86" x2="430" y2="44" stroke="currentColor" strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
                  <line x1="230" y1="86" x2="430" y2="118" stroke="currentColor" strokeWidth="1" strokeDasharray="4 3" opacity="0.5" />
                  <polyline points="10,96 50,101 90,90 130,98 170,82 210,88 230,86" fill="none" stroke="currentColor" strokeWidth="1.6" />
                  <circle cx="230" cy="86" r="4" fill="currentColor" />
                  <text x="424" y="40" textAnchor="end" fontFamily="var(--mono)" fontSize="10" fill="var(--t2)">$181</text>
                  <text x="424" y="128" textAnchor="end" fontFamily="var(--mono)" fontSize="10" fill="var(--t2)">$148</text>
                </svg>
                <div className="fx-mock__row"><span className="l">{T('看涨押注墙', 'Call wall')}<span>$175</span></span><span className="r up">{T('34,000 份', '34,000')}</span></div>
                <div className="fx-mock__row"><span className="l">{T('保护盘', 'Protection')}<span>$160</span></span><span className="r" style={{ color: 'var(--a-data)' }}>{T('17,000 份', '17,000')}</span></div>
              </div>
            </div>
            <div className="fx-floaty" style={{ top: '-18px', right: '-14px' }}>{T('情绪', 'Bias')} <b className="up">{T('看涨 63%', 'Bull 63%')}</b></div>
            <div className="fx-floaty" style={{ bottom: '24px', left: '-22px' }}>{T('目标价 $180 ·', 'Target $180 ·')} <b>28%</b></div>
          </div>
        </div>
      </section>

      {/* ── Product picker ── */}
      <section className="fx-sec fx-picker-sec">
        <div className="fx-wrap">
          <h2>{T('今天想先看哪一块？', 'What do you want to see first?')}</h2>
          <div className="fx-picker">
            {PICKER.map(p => (
              <button className="fx-pk" key={p.key} onClick={() => go(p.key)}>
                <span className="sw" style={{ background: p.color }} />{cn ? p.cn : p.en}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Social proof ── */}
      <section className="fx-sec fx-proof">
        <div className="fx-wrap">
          <p>{T('数据来自全球主流财经源', 'Data from the world’s major financial sources')}</p>
          <div className="row">
            {['Bloomberg', 'Reuters', 'WSJ', 'CNBC', 'Nikkei', 'FT'].map(s => <span className="lg" key={s}>{s}</span>)}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="fx-sec fx-feat">
        <div className="fx-wrap">
          {features.map(f => (
            <div className={`fx-frow${f.rev ? ' rev' : ''}`} key={f.key}>
              <div className="fx-ftext">
                <span className="fx-kick" style={{ color: f.color }}><span className="sw" style={{ background: f.color }} />{f.kick}</span>
                <h3>{f.h}</h3>
                <p className="d">{f.d}</p>
                <div className="fx-flist">
                  {f.li.map((x, i) => <span className="fx-fli" key={i} style={{ '--acc': f.color }}>{CHECK}{x}</span>)}
                </div>
                <button className="fx-flink" style={{ color: f.color }} onClick={() => go(f.key)}>{f.link}</button>
              </div>
              <div className="fx-fvis">
                <div className="fx-fvis__bar"><span className="fx-dot" /><span className="fx-dot" /><span className="fx-dot" /><span className="fx-mock__tab">{f.tab}</span></div>
                {f.vis}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Use case + stats ── */}
      <section className="fx-sec fx-uc">
        <div className="fx-wrap">
          <h2>{T('无论你是哪种交易者', 'Whatever kind of trader you are')}</h2>
          <p className="lede">{T('同一个工作台，按你的节奏读市场。', 'One workspace, read the market at your own pace.')}</p>
          <div className="fx-uc-tabs">
            {ucTabs.map((tb, i) => (
              <button className={`fx-uc-tab${ucTab === i ? ' on' : ''}`} key={i} onClick={() => setUcTab(i)}>{tb}</button>
            ))}
          </div>
          <div className="fx-uc-panel">
            <div className="fx-uc-cap">
              <span className="fx-kick" style={{ color: uc.color }}><span className="sw" style={{ background: uc.color }} />{uc.name}</span>
              <p>{uc.cap}</p>
              <button className="fx-flink" style={{ color: uc.color }} onClick={() => go(uc.key)}>{T('进入', 'Open')}{uc.name} →</button>
            </div>
            <div className="fx-fvis fx-uc-vis">
              <div className="fx-fvis__bar"><span className="fx-dot" /><span className="fx-dot" /><span className="fx-dot" /><span className="fx-mock__tab">{uc.tab}</span></div>
              {uc.vis}
            </div>
          </div>
          <div className="fx-stats">
            {stats.map((s, i) => (
              <div className="fx-stat" key={i}><div className="num">{s.num}</div><div className="lab">{s.lab}</div></div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="fx-sec fx-test">
        <div className="fx-wrap">
          <h2>{T('散户怎么说', 'What retail traders say')}</h2>
          <div className="fx-tgrid">
            {testimonials.map((t, i) => (
              <div className="fx-tcard" key={i}>
                <p className="q">{t.q}</p>
                <div className="who"><div className="av">{t.av}</div><div><div className="nm">{t.nm}</div><div className="rl">{t.rl}</div></div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="fx-final">
        <h2>{T('准备好在噪音里听见信号了吗？', 'Ready to hear the signal in the noise?')}</h2>
        <p>{T('加入 12,000+ 散户，一个工作台读懂市场。', 'Join 12,000+ retail traders — read the market from one workspace.')}</p>
        <div className="fx-capture" style={{ justifyContent: 'center' }}>
          <button className="fx-btn fx-btn--dark" onClick={() => go('option-lens')}>{T('免费开始', 'Get started')} →</button>
          <button className="fx-btn fx-btn--ghost" onClick={() => go('liquidity')}>{T('看流动性浮标 →', 'See Liquidity →')}</button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="fx-foot-wrap">
        <div className="fx-foot">
          <div className="about">
            <span className="fx-brand"><span className="mk">临</span>{T('临象', 'LinXiang')}<span style={{ color: 'var(--t3)' }}>{T('财经', 'Finance')}</span></span>
            <p>{T('散户的金融情报台。把市场的噪音，过滤成你能用的信号。', 'A retail intelligence desk — filtering market noise into signal you can use.')}</p>
          </div>
          <div className="fcol"><h5>{T('产品', 'Product')}</h5><a onClick={() => go('news')}>{T('实时新闻', 'News')}</a><a onClick={() => go('analytics')}>{T('数据分析', 'Analytics')}</a><a onClick={() => go('option-lens')}>{T('期权透镜', 'Options Lens')}</a><a onClick={() => go('liquidity')}>{T('流动性浮标', 'Liquidity')}</a><a onClick={() => go('macro-pulse')}>MacroPulse</a></div>
          <div className="fcol"><h5>{T('资源', 'Resources')}</h5><a onClick={() => go('fortune')}>{T('每日一卦', 'Daily fortune')}</a><a onClick={() => go('weekly-mindmap')}>{T('每周周报', 'Weekly report')}</a><a>{T('使用指南', 'Guide')}</a><a>{T('API 文档', 'API docs')}</a></div>
          <div className="fcol"><h5>{T('公司', 'Company')}</h5><a>{T('关于我们', 'About')}</a><a>{T('定价', 'Pricing')}</a><a>{T('联系我们', 'Contact')}</a></div>
          <div className="fcol"><h5>{T('法务', 'Legal')}</h5><a>{T('隐私政策', 'Privacy')}</a><a>{T('使用条款', 'Terms')}</a><a>{T('风险提示', 'Risk')}</a></div>
        </div>
        <div className="fx-foot-btm">{T('© 2026 临象财经 · 投资有风险，入市需谨慎', '© 2026 LinXiangFinance · Investment involves risk')}</div>
      </footer>
    </div>
  );
};

export default FxLabHome;
